import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { ghs, toPesewas } from '../lib/money.js';
import { Button, Chip, Banner } from '../components/ui.jsx';
import { Sheet, Divider, Section } from './SalesPoint.jsx';
import { printElement } from '../lib/print.js';
import { useDateRange, DateRangeBar } from './DateRange.jsx';

const card = { background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' };

// ---------------------------------------------------------------- transactions

export function Transactions({ slug, canVoid }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [status, setStatus] = useState('completed');
  const [data, setData] = useState({ sales: [], totals: { n: 0, total: 0 } });
  const [open, setOpen] = useState(null);

  const load = () => b.sales({ status }).then(setData).catch(() => {});
  useEffect(() => { load(); }, [status, slug]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {[['completed', 'Sales'], ['voided', 'Voided']].map(([id, label]) => (
          <Button key={id} size="m" variant={status === id ? 'primary' : 'ghost'}
            style={status === id ? {} : { border: '1px solid var(--color-border-default)' }}
            onClick={() => setStatus(id)}>{label}</Button>
        ))}
        <span style={{ flex: 1 }} />
        <span className="t-body-m secondary">{data.totals.n} sales · {ghs(data.totals.total)}</span>
      </div>

      <div style={card}>
        <HeadRow cols={['REF', 'TIME', 'CUSTOMER', 'STAFF', 'ITEMS', 'PAYMENT', 'TOTAL']} />
        {data.sales.map(s => (
          <button key={s.id} onClick={() => b.sale(s.id).then(d => setOpen(d.sale))}
            style={{
              display: 'flex', gap: 12, alignItems: 'center', width: '100%', padding: '12px 16px',
              borderTop: '1px solid var(--color-border-default)', background: 'transparent',
              border: 'none', borderTopStyle: 'solid', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit'
            }}>
            <span className="money t-money-s" style={{ width: 132 }}>{s.ref}</span>
            <span className="money t-money-s secondary" style={{ width: 64 }}>{time(s.sold_at)}</span>
            <span className="t-body-m" style={{ width: 150 }}>{s.customer_name || 'Walk-in'}</span>
            <span className="t-body-m secondary" style={{ width: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.staff || '-'}</span>
            <span className="t-body-m secondary" style={{ width: 52 }}>{s.item_count}</span>
            <span className="t-body-s secondary" style={{ width: 120 }}>{(s.methods || '').split(',').map(cap).join(', ')}</span>
            <span style={{ flex: 1 }} />
            <span className="money t-money-m" style={{ width: 120, textAlign: 'right' }}>{ghs(s.total)}</span>
            {s.balance > 0 && <Chip tone="danger">Owes {ghs(s.balance)}</Chip>}
          </button>
        ))}
        {!data.sales.length && <Empty>No {status === 'voided' ? 'voided sales' : 'sales yet today'}.</Empty>}
      </div>

      {open && <SaleDetail sale={open} slug={slug} canVoid={canVoid}
        onClose={() => setOpen(null)} onVoided={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function SaleDetail({ sale, slug, canVoid, onClose, onVoided }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [error, setError] = useState(null);

  async function doVoid() {
    if (!reason.trim()) { setError('A reason is required.'); return; }
    try {
      await b.voidSale(sale.id, reason.trim());
      onVoided();
    } catch (e) { setError(e.data?.error || 'That did not work.'); }
  }

  return (
    <Sheet title={sale.ref} subtitle={`${new Date(sale.sold_at).toLocaleString('en-GB')} · ${sale.customer_name || 'Walk-in'}`} onClose={onClose}>
      <Section label="LINES, STAFF AND COMMISSION">
        {sale.items.map(it => (
          <div key={it.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0' }}>
            <div style={{ flex: 1 }}>
              <div className="t-body-m">{it.name}</div>
              <div className="t-body-s muted">
                {it.staff_name || 'Unassigned'}{it.commission_value ? ` · ${it.commission_value}% of line net` : ''}
              </div>
            </div>
            <span className="money t-money-s" style={{ color: 'var(--color-status-success)' }}>{ghs(it.commission_amount)}</span>
            <span className="money t-money-m">{ghs(it.line_total)}</span>
          </div>
        ))}
      </Section>
      <Divider />
      <Section label="TOTALS">
        <KV k="Subtotal" v={ghs(sale.subtotal)} />
        {sale.discount_total > 0 && <KV k="Discount" v={'-' + ghs(sale.discount_total)} />}
        <KV k="Total" v={ghs(sale.total)} strong />
        <KV k="Commission total" v={ghs(sale.commission_total)} tone="var(--color-status-success)" />
        {sale.tip_total > 0 && <KV k="Tips" v={ghs(sale.tip_total)} />}
        {sale.balance > 0 && <KV k="Balance owed" v={ghs(sale.balance)} tone="var(--color-status-danger)" />}
      </Section>
      <Divider />
      <Section label="PAYMENTS">
        {sale.payments.map(p => (
          <KV key={p.id} k={p.method === 'momo' ? `MoMo ${p.network || ''}` : cap(p.method)} v={ghs(p.amount)} />
        ))}
      </Section>

      {sale.status === 'voided' ? (
        <Section label="VOIDED">
          <Banner tone="danger" title="This sale was voided">{sale.void_reason}</Banner>
        </Section>
      ) : canVoid && (
        <>
          <Divider />
          <Section label="VOID WITH A REASON">
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder="Why is this being voided?"
              style={{
                width: '100%', padding: 12, borderRadius: 'var(--radius-md)',
                border: '2px solid var(--color-status-danger)', background: 'var(--color-bg-surface)',
                color: 'var(--color-text-primary)', font: '400 15px/22px var(--font-ui)', resize: 'vertical'
              }} />
            <div className="t-body-s muted">
              Voiding never deletes. The sale keeps its lines and its reference, the commission stops
              counting, and your name and reason go to the audit log.
            </div>
            {error && <Banner tone="danger">{error}</Banner>}
            <Button variant="danger" disabled={voiding} onClick={doVoid}>Void this sale</Button>
          </Section>
        </>
      )}
      <div style={{ height: 24 }} />
    </Sheet>
  );
}

// ---------------------------------------------------------------- commission

export function Commission({ slug }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const range = useDateRange('all');
  const { params, label } = range;
  const [data, setData] = useState(null);
  const sheet = useRef(null);
  useEffect(() => { b.report('commission', params).then(setData).catch(() => {}); }, [slug, params]);
  if (!data) return <div className="t-body-m muted">Loading the payout sheet</div>;

  const t = data.totals;

  /**
   * Nobody at Ezel Spa is on commission: the team is salaried. A sheet of zeros headed
   * "payout" would imply money is owed that is not, so when there is nothing to pay the
   * screen says what it actually is, which is a record of who did what work.
   *
   * Detected rather than configured. The moment somebody is put on a percentage the sheet
   * becomes a payout sheet again on its own.
   */
  const anyoneOnCommission = t.commission > 0;
  const filename = anyoneOnCommission
    ? `Commission payout ${label}`
    : `Work by staff ${label}`;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <DateRangeBar {...range} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} className="no-print">
        <Chip tone="accent">{label}</Chip>
        <span className="t-body-m secondary">
          {anyoneOnCommission
            ? 'The payout sheet. This is what gets handed over on payday.'
            : 'Who did what, and what it brought in. Nobody here is on commission, so this owes nobody anything: it is a record of work, not a payslip.'}
        </span>
        <span style={{ flex: 1 }} />
        <Button variant="secondary" size="m"
          onClick={() => printElement(sheet.current, { title: filename, page: 'size: A4 portrait; margin: 14mm;' })}>
          {anyoneOnCommission ? 'Print payout sheet' : 'Print the work sheet'}
        </Button>
      </div>

      <div style={card} ref={sheet}>
        {/* On paper there is no date picker to look at, so the period has to be on the sheet.
            A payout sheet that does not say what it covers is the kind of thing that gets
            paid twice. */}
        <div style={{ padding: '14px 16px' }}>
          <div className="t-label-xs muted">PERIOD</div>
          <div className="t-body-m">{label}</div>
        </div>
        <HeadRow cols={anyoneOnCommission
          ? ['STAFF', 'LINES', 'REVENUE', 'RATE', 'COMMISSION DUE']
          : ['STAFF', 'LINES', 'REVENUE BROUGHT IN', '', '']} />
        {data.staff.map(r => (
          <div key={r.staff_id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '13px 16px', borderTop: '1px solid var(--color-border-default)' }}>
            <span className="t-body-l" style={{ flex: 1 }}>{r.staff}</span>
            <Num w={70}>{r.lines}</Num>
            <Num w={140}>{ghs(r.revenue)}</Num>
            <Num w={60} dim>{r.commission_value}%</Num>
            <Num w={150} tone="var(--color-status-success)" strong>{ghs(r.due)}</Num>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '16px', background: 'var(--color-bg-subtle)', borderTop: '1px solid var(--color-border-default)' }}>
          <span className="t-heading-m" style={{ flex: 1 }}>{data.staff.length} staff</span>
          <Num w={70}>{t.lines}</Num>
          <Num w={140}>{ghs(t.revenue)}</Num>
          <Num w={60} />
          <Num w={150} tone="var(--color-status-success)" strong>{ghs(t.due)}</Num>
        </div>
        {data.unassigned.lines > 0 && (
          <div style={{ padding: 16, borderTop: '1px solid var(--color-border-default)' }}>
            <Banner tone="warning" title={`${data.unassigned.lines} line${data.unassigned.lines === 1 ? '' : 's'} had no staff assigned`}>
              Worth {ghs(data.unassigned.revenue)} of revenue that pays nobody commission. Assign the staff on the
              sale line and this figure drops to zero.
            </Banner>
          </div>
        )}
        <div style={{ padding: '12px 16px 16px' }} className="t-body-s muted">
          Every figure comes from commission written onto sale lines at the moment of sale, so this
          reconciles to the transactions list exactly. Voided sales are excluded.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- cash up

export function CashUp({ slug }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [state, setState] = useState(null);
  const [float, setFloat] = useState('200.00');
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const load = () => b.cashSession().then(setState).catch(() => {});
  useEffect(() => { load(); }, [slug]);
  if (!state) return <div className="t-body-m muted">Loading</div>;

  if (done) {
    return (
      <div style={{ ...card, padding: 24, display: 'grid', gap: 12, maxWidth: 620 }}>
        <div className="t-heading-l">Drawer closed</div>
        <KV k="Expected" v={ghs(done.expected)} />
        <KV k="Counted" v={ghs(done.counted)} />
        <KV k="Variance" v={ghs(done.variance)} strong
          tone={done.variance === 0 ? 'var(--color-status-success)' : 'var(--color-status-danger)'} />
        <div className="t-body-s muted">
          {done.variance === 0 ? 'It balanced.' : 'The variance and its reason are in the audit log.'}
        </div>
        <Button onClick={() => { setDone(null); load(); }}>Done</Button>
      </div>
    );
  }

  if (!state.session) {
    return (
      <div style={{ ...card, padding: 24, display: 'grid', gap: 16, maxWidth: 620 }}>
        <div>
          <div className="t-heading-l">Open the drawer</div>
          <div className="t-body-m secondary">Count the float in before the first sale. Without this nobody can prove the drawer balanced.</div>
        </div>
        <MoneyField label="OPENING FLOAT" value={float} onChange={setFloat} />
        <Button onClick={() => b.openSession(float).then(load)}>Open with {ghs(toPesewas(float))}</Button>
      </div>
    );
  }

  const expected = state.expected;
  const variance = counted === '' ? null : toPesewas(counted) - expected;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ ...card, padding: 0, width: 520 }}>
        <div style={{ padding: '18px 20px 14px' }} className="t-label-xs muted">EXPECTED IN DRAWER</div>
        <Divider />
        <KVRow k="Opening float" v={ghs(state.session.opening_float)} />
        <KVRow k="Cash sales" v={ghs(state.cashTaken)} />
        <Divider />
        <KVRow k="Expected" v={ghs(expected)} big />
        {variance !== null && <KVRow k="Counted" v={ghs(toPesewas(counted))} big />}
        {variance !== null && (
          <div style={{
            padding: '18px 20px',
            background: variance === 0 ? 'var(--color-status-success-bg)' : 'var(--color-status-danger-bg)',
            color: variance === 0 ? 'var(--color-status-success)' : 'var(--color-status-danger)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="t-label-xs" style={{ flex: 1 }}>VARIANCE</span>
              <span className="money t-money-xl">{ghs(variance)}</span>
            </div>
            {variance !== 0 && <div className="t-body-s" style={{ marginTop: 4 }}>A reason is required before this session can close.</div>}
          </div>
        )}
      </div>

      <div style={{ ...card, padding: 20, width: 420, display: 'grid', gap: 14 }}>
        <div className="t-label-xs muted">COUNT THE DRAWER</div>
        <MoneyField label="COUNTED CASH" value={counted} onChange={setCounted} />
        <div className="field">
          <label className="t-label-xs">REASON, IF IT DOES NOT BALANCE</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Short change given, and so on" />
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <Button disabled={counted === ''} onClick={async () => {
          setError(null);
          try {
            const r = await b.closeSession({ countedCash: counted, note: note || null });
            setDone(r);
          } catch (e) {
            setError(e.data?.error === 'reason_required_for_variance'
              ? 'The drawer does not balance. Say why before closing.'
              : 'That did not work.');
          }
        }}>Close session</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- reports

export function Reports({ slug }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const range = useDateRange('all');
  const { params } = range;
  const [summary, setSummary] = useState(null);
  const [day, setDay] = useState([]);
  const [staff, setStaff] = useState([]);
  const [service, setService] = useState([]);
  const [payment, setPayment] = useState([]);

  useEffect(() => {
    b.report('summary', params).then(setSummary).catch(() => {});
    b.report('by-day', params).then(d => setDay(d.rows)).catch(() => {});
    b.report('by-staff', params).then(d => setStaff(d.rows)).catch(() => {});
    b.report('by-service', params).then(d => setService(d.rows)).catch(() => {});
    b.report('by-payment', params).then(d => setPayment(d.rows)).catch(() => {});
  }, [slug, params]);

  if (!summary) return <div className="t-body-m muted">Loading reports</div>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <DateRangeBar {...range} />

      {!summary.sales && (
        <div className="t-body-m muted" style={{ ...card, padding: 20 }}>
          No completed sales in this period. Voided sales never count.
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Kpi label="REVENUE" value={ghs(summary.revenue)} note={`${summary.sales} sales`} />
        <Kpi label="AVERAGE SALE" value={ghs(summary.average)} note="per sale" />
        <Kpi label="COMMISSION" value={ghs(summary.commission)} note="owed on these sales" />
        <Kpi label="DISCOUNTS" value={ghs(summary.discounts)} note="given away" />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Table title="BY DAY" rows={day.map(r => [r.day, `${r.sales} sales`, ghs(r.revenue)])} />
        <Table title="BY STAFF" rows={staff.map(r => [r.staff, `${r.lines} lines`, ghs(r.revenue)])} />
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Table title="BY SERVICE" rows={service.map(r => [r.name, `${r.times}x`, ghs(r.revenue)])} />
        <Table title="BY PAYMENT METHOD" rows={payment.map(r => [cap(r.method), `${r.count}`, ghs(r.amount)])} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- bits

const Kpi = ({ label, value, note }) => (
  <div style={{ ...card, padding: 18, flex: 1, minWidth: 220 }}>
    <div className="t-label-xs muted">{label}</div>
    <div className="money t-money-xl" style={{ whiteSpace: 'nowrap' }}>{value}</div>
    <div className="t-body-s muted">{note}</div>
  </div>
);

const Table = ({ title, rows }) => (
  <div style={{ ...card, flex: 1, minWidth: 380 }}>
    <div style={{ padding: '14px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">{title}</div>
    {rows.map((r, i) => (
      <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 16px', borderTop: '1px solid var(--color-border-default)' }}>
        <span className="t-body-m" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[0]}</span>
        <span className="t-body-s muted">{r[1]}</span>
        <span className="money t-money-m">{r[2]}</span>
      </div>
    ))}
    {!rows.length && <Empty>Nothing yet.</Empty>}
  </div>
);

const HeadRow = ({ cols }) => (
  <div style={{ display: 'flex', gap: 12, padding: '12px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
    {cols.map(c => <span key={c} style={{ flex: c === cols[0] ? 1 : 'none' }}>{c}</span>)}
  </div>
);

const Num = ({ children, w, strong, dim, tone }) => (
  <span className={strong ? 'money t-money-m' : 'money t-money-s'}
    style={{ width: w, textAlign: 'right', color: tone || (dim ? 'var(--color-text-muted)' : 'var(--color-text-primary)'), whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const KV = ({ k, v, strong, tone }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span className={strong ? 't-heading-s' : 't-body-m secondary'} style={{ flex: 1 }}>{k}</span>
    <span className={strong ? 'money t-money-l' : 'money t-money-m'} style={{ color: tone }}>{v}</span>
  </div>
);

const KVRow = ({ k, v, big }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '12px 20px' }}>
    <span className={big ? 't-heading-m' : 't-body-m secondary'} style={{ flex: 1 }}>{k}</span>
    <span className={big ? 'money t-money-xl' : 'money t-money-m'}>{v}</span>
  </div>
);

const MoneyField = ({ label, value, onChange }) => (
  <div className="field">
    <label className="t-label-xs">{label}</label>
    <input className="money" value={value} inputMode="decimal" onChange={e => onChange(e.target.value)}
      placeholder="0.00" style={{ fontSize: 20 }} />
  </div>
);

const Empty = ({ children }) => <div style={{ padding: 32, textAlign: 'center' }} className="t-body-m muted">{children}</div>;

const time = t => new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const cap = s => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1);
